import { ref, computed } from 'vue';
import store from '@/store';
import router from '@/router';

/**
 * Composable para gestionar el registro en la preventa de un evento.
 *
 * Se encarga de obtener la información del evento para la preventa,
 * comprobar el estado del registro del usuario y enviar la solicitud
 * de registro en la preventa.
 *
 * @param {import('vue').Ref<string|number>} eventoIdRef Ref reactiva con el ID del evento.
 * @param {import('vue').Ref<string|number>} sesionIdRef Ref reactiva con el ID de la sesión.
 * @returns {{
 *   event: import('vue').Ref<Object|null>,
 *   validationErrors: import('vue').Ref<Object|null>,
 *   paso: import('vue').Ref<number>,
 *   loading: import('vue').Ref<boolean>,
 *   configuracion: import('vue').ComputedRef<any>,
 *   user: import('vue').ComputedRef<any>,
 *   getEvent: () => Promise<void>,
 *   solicitarRegistro: (datosRegistro: Object) => Promise<void>
 * }} Objeto con el estado y las acciones para el registro en preventa.
 */

export function useRegistroPreventa(eventoIdRef, sesionIdRef) {
    const event = ref(null);
    const paso = ref(1);
    const loading = ref(false);
    const validationErrors = ref(null);

    const configuracion = computed(() => store.getters.getConfig);
    const user = computed(() => store.getters.getUser);

    const getEvent = async() => {
        const params = {
            filters: {
                tid: configuracion.value.hash_teatro
            },
            event_id: eventoIdRef.value,
            sesion_id: sesionIdRef.value
        };

        try {
            const res = await store.dispatch('FRONT_EVENTS_SHOW', params);

            event.value = res.data.data;

            if (
                !event.value?.preventa?.registro_preventa_activo ||
                event.value?.preventa?.registrado_en_preventa ||
                event.value?.status != 1
            ) {
                router.push({ name: 'front.eventos.index' });
            }

        } catch (e) {
            router.push({ name: 'front.eventos.index' });
        }
    };

    const solicitarRegistro = async datosRegistro => {
        validationErrors.value = null;
        loading.value = true;

        let registro = { ...datosRegistro };

        delete registro.tipoDocumentoSeleccionado;
        delete registro.provincia;
        delete registro.localidad;

        const params = {
            filters: {},
            evento_id: eventoIdRef.value,
            ...registro,
            comunicaciones: 1
        };
        try {
            await store.dispatch('FRONT_EVENTS_REGISTRAR_PREVENTA', params);

            store.commit('setFrontEvents', null);
            store.commit('setFrontExperiencias', null);

            paso.value++;
        } catch (e) {
            if (e.response?.data?.errors) {
                validationErrors.value = e.response.data.errors;
            }

            console.error(e);
        } finally {
            loading.value = false;
        }
    };

    return {
        event,
        validationErrors,
        paso,
        loading,
        configuracion,
        user,
        getEvent,
        solicitarRegistro
    };
}
